import { WorkloadType } from '@/components/Workload/types';
import { usePathTo } from '@/utils/useUrlParams';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { WorkloadParams } from '../types';

const { DEPLOYMENT } = WorkloadType;

/**
 * @description 自定义 Hook，用于管理和同步 URL 中的工作负载类型 (`:workload` 参数)。
 *
 * 此 Hook 负责从 React Router 的 URL 参数中读取 `:workload`，
 * 验证其是否为有效的 `WorkloadType`。如果 URL 中的 `:workload` 参数
 * 无效或缺失，它会自动将 URL 重定向到默认的工作负载类型 (`DEPLOYMENT`)
 * 对应的路径，并确保返回的 `currentWorkload` 是有效的。
 *
 * 它还提供了一个 `handleTabChange` 回调函数，用于在用户切换工作负载类型时
 * 更新 URL 中的 `:workload` 参数，从而触发页面内容的更新。
 *
 * @requires react-router-dom - 依赖 `useParams`, `usePathTo`, `useLocation` 来与路由交互。
 * @requires 路由配置 - 使用此 Hook 的组件所在的路由路径必须包含 `:workload` 动态段。
 *           例如: `/workload/:workload`
 *
 * @returns {{
 *   currentWorkload: WorkloadType; // 当前有效（经过验证或设置为默认值）的工作负载类型。
 *   handleTabChange: (newWorkload: WorkloadType) => void; // 用于切换工作负载类型并更新 URL 的回调函数。
 * }}
 * ```
 */
export const useWorkloadTabs = () => {
  // 读取路径参数 /:workload 的当前值
  const { workload } = useParams<WorkloadParams>();
  const navigate = usePathTo();
  const location = useLocation();
  // 使用 useMemo 计算当前有效的 workload 类型
  const currentEffectiveWorkload = useMemo(() => {
    return isValidWorkloadType(workload) ? workload : DEPLOYMENT;
  }, [workload]);

  // 在 workload 无效时修正 URL
  useEffect(() => {
    // 只有当 URL 中的 workload 参数存在且无效时才执行修正
    if (workload && !isValidWorkloadType(workload)) {
      const pathSegments = location.pathname.split('/');
      // 查找无效 workload 在路径中的位置
      const workloadParamIndex = pathSegments.findIndex((segment) => segment === workload);

      if (workloadParamIndex !== -1) {
        // 构建修正后的路径
        const newPathSegments = [...pathSegments];
        newPathSegments[workloadParamIndex] = DEPLOYMENT; // 替换为默认值
        const correctedPath = newPathSegments.join('/');
        navigate(correctedPath, { replace: true });
      }
    }
  }, [workload, location.pathname, navigate]);

  const handleTabChange = useCallback(
    (newWorkload: WorkloadType) => {
      const pathSegments = location.pathname.split('/');
      // 查找当前有效 workload (currentEffectiveWorkload) 在路径中的位置
      // 注意：此时路径可能已经被 useEffect 修正过
      const currentWorkloadIndex = pathSegments.findIndex(
        (segment) => segment === currentEffectiveWorkload,
      );

      if (currentWorkloadIndex !== -1) {
        // 构建新路径
        const newPathSegments = [...pathSegments];
        newPathSegments[currentWorkloadIndex] = newWorkload;
        const newPath = newPathSegments.join('/');
        // 只有当路径实际改变时才导航
        if (newPath !== location.pathname) {
          navigate(newPath);
        }
      }
    },
    [navigate, location.pathname, currentEffectiveWorkload],
  );

  return {
    // 返回当前有效的 workload 类型
    currentWorkload: currentEffectiveWorkload,
    handleTabChange,
  };
};

function isValidWorkloadType(value: string): value is WorkloadType {
  return Object.values(WorkloadType).includes(value as WorkloadType);
}
